<script setup lang="ts">
import { nextTick } from 'vue'
import Dialog from 'primevue/dialog'

export interface BaseModalProps {
  visible: boolean
  header?: string
  width?: string
  closable?: boolean
  modal?: boolean
  position?: 'center' | 'top'
}

const props = withDefaults(defineProps<BaseModalProps>(), {
  width: '500px',
  closable: true,
  modal: true,
  position: 'center',
})

const emit = defineEmits<{
  'update:visible': [value: boolean]
  hide: []
  show: []
}>()

function onUpdateVisible(value: boolean) {
  emit('update:visible', value)
}

function onHide() {
  emit('hide')
}

function onShow() {
  emit('show')
  nextTick(() => {
    const dialog = document.querySelector('[role="dialog"]')
    if (!dialog) return
    const focusable = dialog.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    )
    if (focusable.length > 0) {
      focusable[0].focus()
    }
  })
}
</script>

<template>
  <Dialog
    :visible="props.visible"
    :header="props.header"
    :closable="props.closable"
    :modal="props.modal"
    :position="props.position"
    :style="{ width: props.width }"
    :pt="{
      root: {
        class: 'bg-white rounded-lg shadow-dialog ring-1 ring-black/5 p-5',
        role: 'dialog',
        'aria-modal': 'true',
      },
      header: {
        class: 'text-lg font-bold text-text-primary pb-4 border-b border-border',
      },
      mask: {
        class: 'bg-black/50 backdrop-blur-sm',
      },
      closeButton: {
        class: 'rounded-md p-1 text-text-muted transition-colors duration-fast ease-smooth hover:bg-bg-tertiary hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-primary',
        'aria-label': 'Close',
      },
      transition: {
        enterFromClass: 'opacity-0 scale-95',
        enterActiveClass: 'transition-all duration-base ease-enter',
        enterToClass: 'opacity-100 scale-100',
        leaveFromClass: 'opacity-100 scale-100',
        leaveActiveClass: 'transition-all duration-fast ease-exit',
        leaveToClass: 'opacity-0 scale-95',
      },
    }"
    @update:visible="onUpdateVisible"
    @hide="onHide"
    @show="onShow"
  >
    <template v-if="$slots.header" #header>
      <slot name="header" />
    </template>

    <slot />

    <template v-if="$slots.footer" #footer>
      <slot name="footer" />
    </template>
  </Dialog>
</template>
